import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useApi from "../CustomHooks/useApi";
import { useNavigate } from "react-router-dom";


const Subscription = () => {
  const username = useSelector(
    (state) => state.auth.employeeDetails?.username
  );

  const role = useSelector(
    (state) => state.auth.employeeDetails?.role
  );

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("SILVER");
  // const [durationDays, setDurationDays] = useState(1);
  // const [isAutoRenewal, setIsAutoRenewal] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [closingSubscription, setClosingSubscription] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);


  const subscriptionIdMap = {
    SILVER: "SILVER",
    GOLD: "MONTHLY_BASIC",
    PLATINUM: "MONTHLY",
  };

  const planTypeMap = {
    SILVER: "DAILY",
    GOLD: "MONTHLY_BASIC",
    PLATINUM: "MONTHLY",
  };

  const planBenefits = {
    SILVER: {
      title: "AEPS + DMT Combined Subscription",
      type: "DAILY",
      originalPrice: "₹33",
      price: "₹20/ DAY",
      benefits: ["AEPS Service", "DMT Service"],
      aeps: [
        "0-500 → ₹0",
        "501-1000 → ₹1",
        "1001-1500 → ₹2",
        "1501-2000 → ₹4",
        "2001-2500 → ₹6",
        "2501-3000 → ₹7",
        "3000-9000 → ₹11",
        "9000-10000 → ₹12",
      ],
      dmt: ["Above ₹1000 → 0.45%"],
      ccbp: ["1-25000 → ₹25"],
    },

    GOLD: {
      title: "AEPS + DMT Combined Subscription",
      type: "MONTHLY",
      originalPrice: "₹799",
      price: "₹299 / Month",
      benefits: ["AEPS Service", "DMT Service"],
      aeps: [
        "0-500 → ₹0",
        "501-1000 → ₹1",
        "1001-1500 → ₹2",
        "1501-2000 → ₹4",
        "2001-2500 → ₹6",
        "2501-3000 → ₹7",
        "3000-9000 → ₹11",
        "9000-10000 → ₹12",
      ],
      dmt: ["Above ₹1000 → 0.45%"],
      ccbp: ["1-25000 → ₹25"],
    },

    PLATINUM: {
      title: "AEPS + CCBP + DMT Combined Subscription",
      type: "MONTHLY",
      originalPrice: "₹1300",
      price: "₹999 / Month",
      benefits: ["AEPS Service", "Credit Card Bill Payments", "DMT Service"],
      aeps: [
        "0-500 → ₹0",
        "501-1000 → ₹2",
        "1001-1500 → ₹4",
        "1501-2000 → ₹6",
        "2001-2500 → ₹8",
        "2501-3000 → ₹10",
        "3000-10000 → ₹13",
      ],
      ccbp: ["1-25000 → ₹25"],
      dmt: ["Above ₹1000 → 0.45%"],
    },
  };

  // Custom Popup State
  const [popup, setPopup] = useState({
    isOpen: false,
    message: "",
    type: "success", // 'success' or 'error'
  });

  const { callApi: subscribeApi } = useApi(
    "/subscription/subscribe",
    "POST"
  );

  const { callApi: renewSubscriptionApi } = useApi(
    `/subscription/renew/${username}`,
    "POST"
  );

  const { callApi: historyApi } = useApi(
    `/subscription/history/${username}`,
    "GET"
  );

  const { callApi: closeSubscriptionApi } = useApi(
    `/subscription/close/${username}`,
    "POST"
  );

  const showPopup = (message, type = "success") => {
    setPopup({ isOpen: true, message, type });
  };

  const closePopup = () => {
    setPopup((prev) => ({ ...prev, isOpen: false }));
  };

  const fetchSubscriptionHistory = async () => {
    try {
      setHistoryLoading(true);

      const res = await historyApi();

      if (res?.success) {
        setSubscriptionHistory(res?.history || []);
      }
    } catch (error) {
      console.error("History fetch failed", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Auto-fetch history on page open
  // useEffect(() => {
  //   if (username) {
  //     fetchSubscriptionHistory();
  //   }
  // }, [username]);

  useEffect(() => {
    if (username && role === "MERCHANT") {
      fetchSubscriptionHistory();
    }
    // eslint-disable-next-line
  }, [username, role]);

  const handleRenewSubscription = async () => {
    try {
      setRenewLoading(true);

      const res = await renewSubscriptionApi();

      showPopup(
        res?.message || "Monthly subscription renewed successfully",
        "success"
      );

      fetchSubscriptionHistory();
    } catch (error) {
      const apiErrorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Renewal failed";

      showPopup(apiErrorMessage, "error");
    } finally {
      setRenewLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setLoading(true);

      const payload = {
        username,
        subscriptionId: subscriptionIdMap[selectedPlan],
        planType: planTypeMap[selectedPlan],
        durationDays: planBenefits[selectedPlan].type === "MONTHLY" ? 30 : 1,
      };

      const res = await subscribeApi(payload);

      showPopup(res?.message || "Subscription activated successfully", "success");
      fetchSubscriptionHistory();
    } catch (error) {
      const apiErrorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Subscription failed";

      showPopup(apiErrorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const activateWithPlan = async (plan, e) => {
    e.stopPropagation();
    setSelectedPlan(plan);
    try {
      setLoading(true);
      const payload = {
        username,
        subscriptionId: subscriptionIdMap[plan],
        planType: planTypeMap[plan],
        durationDays: planBenefits[plan].type === "MONTHLY" ? 30 : 1,
      };
      const res = await subscribeApi(payload);
      showPopup(res?.message || "Subscription activated successfully", "success");
      fetchSubscriptionHistory();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.data?.message || error?.message || "Subscription failed";
      showPopup(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSubscription = async () => {
    try {
      setClosingSubscription(true);

      const res = await closeSubscriptionApi();

      showPopup(res?.message || "Subscription close requested successfully", "success");
      fetchSubscriptionHistory();
    } catch (error) {
      const apiErrorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Failed to close subscription";

      showPopup(apiErrorMessage, "error");
    } finally {
      setClosingSubscription(false);
    }
  };



  if (role !== "MERCHANT") {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100 text-gray-800">
        <div className="max-w-md text-center bg-white p-8 rounded-2xl shadow-lg">
          <h1 className="text-4xl font-bold text-red-600 mb-4">Access Denied</h1>

          <p className="text-lg text-gray-600">
            You are not authorized to view this page. Only Merchants can access Subscription details.
          </p>

          <button
            onClick={() => navigate(-1)}
            className="mt-6 inline-block bg-[#12345A] text-white px-6 py-2 rounded-lg shadow hover:opacity-90 transition-all"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f8fb] p-4 md:p-8 relative">
      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-[#12345A] to-[#1E4D8C] rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-52 h-52 bg-yellow-400/10 rounded-full blur-3xl" />

          <div className="flex items-center gap-5 relative z-10">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center text-5xl font-bold text-[#12345A] shadow-xl border-4 border-white">
              ₹
            </div>

            <div>
              <h1 className="text-4xl font-bold">Premium Subscription</h1>

              <p className="text-slate-200 mt-2 text-sm md:text-base">
                Unlock more savings, extra earnings and premium benefits.
              </p>
            </div>
          </div>
        </div>

        {/* BENEFITS */}
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* DMT */}
          <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-200 hover:shadow-xl transition">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-yellow-100 flex items-center justify-center text-3xl">
                💸
              </div>

              <div>
                <h2 className="text-xl font-bold text-[#12345A]">
                  DMT Benefits
                </h2>

                <p className="text-slate-500 text-sm">
                  Save more on every transaction
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-2xl p-5">

              {/* <p className="text-lg font-semibold text-[#12345A]">
                Automatic 10% Discount
              </p> */}

              {/* <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                Get an automatic 10% discount on all DMT agent charges and fees.
              </p> */}
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                Smart DMT, Lower Deductions
              </p>
            </div>
          </div>

          {/* AEPS */}
          <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-200 hover:shadow-xl transition">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center text-3xl">
                🏦
              </div>

              <div>
                <h2 className="text-xl font-bold text-[#12345A]">
                  AEPS Benefits
                </h2>

                <p className="text-slate-500 text-sm">
                  Earn extra commission
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-2xl p-5">
              {/* <p className="text-lg font-semibold text-[#12345A]">
                ₹1 Extra Commission
              </p> */}

              <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                AEPS - Earn More with Every Transaction
              </p>
            </div>
          </div>
        </div>

        {/* PLAN CARDS */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-[#12345A]">Choose Your Plan</h2>
              <p className="text-sm text-slate-400 mt-1">Click a card to select your subscription</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-stretch">

            {/* ── Helper: Slab Table ── */}
            {/* Defined inline below per plan */}

            {/* SILVER */}
            {(() => {
              const active = selectedPlan === "SILVER";
              return (
                <div
                  onClick={() => setSelectedPlan("SILVER")}
                  className={`relative cursor-pointer rounded-3xl overflow-hidden transition-all duration-300 flex flex-col ${active
                      ? "shadow-2xl scale-[1.02] ring-2 ring-slate-400"
                      : "shadow-md hover:shadow-xl hover:scale-[1.01] border border-slate-200"
                    }`}
                >
                  {/* Card Header */}
                  <div className={`p-6 ${active ? "bg-gradient-to-br from-slate-700 to-slate-500" : "bg-gradient-to-br from-slate-100 to-slate-200"}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${active ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"}`}>1</div>
                        <div>
                          <p className={`text-xl font-extrabold ${active ? "text-white" : "text-slate-700"}`}>Plan 1</p>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-slate-300 text-slate-600"}`}>DAILY</span>
                        </div>
                      </div>
                      {active && <span className="text-xs bg-white text-slate-700 font-bold px-3 py-1 rounded-full shadow">✓ Selected</span>}
                    </div>
                    <div className="mt-2">
                      <span className={`text-xs line-through ${active ? "text-slate-300" : "text-red-400"}`}>33.30</span>
                      <p className={`text-4xl font-black mt-0.5 ${active ? "text-white" : "text-slate-800"}`}>
                        ₹23.60 <span className={`text-sm font-semibold ${active ? "text-slate-300" : "text-slate-400"}`}>/ day</span>
                      </p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="bg-white flex-1 p-5 flex flex-col gap-4">
                    {/* Services */}
                    <div className="flex flex-wrap gap-2">
                      {planBenefits.SILVER.benefits.map((b, i) => (
                        <span key={i} className="flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                          <span className="text-green-500">✓</span> {b}
                        </span>
                      ))}
                      <span className="flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-400 text-xs font-semibold px-3 py-1.5 rounded-full line-through">
                        <span className="text-red-400">✕</span> Credit Card Bill Payments
                      </span>
                    </div>

                    {/* AEPS Slabs */}
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="bg-slate-700 text-white text-xs font-bold px-4 py-2 flex items-center gap-2">
                        <span>🏦</span> AEPS Commission Slabs
                      </div>
                      {planBenefits.SILVER.aeps.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className={`flex justify-between items-center px-4 py-2 text-xs ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                            <span className="text-slate-500">{range?.trim()}</span>
                            <span className="font-bold text-slate-700">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* DMT */}
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="bg-amber-500 text-white text-xs font-bold px-4 py-2 flex items-center gap-2">
                        <span>💸</span> DMT — Domestic Money Transfer
                      </div>
                      {planBenefits.SILVER.dmt.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className="flex justify-between items-center px-4 py-2 text-xs bg-white">
                            <span className="text-slate-500">{range?.trim()}</span>
                            <span className="font-bold text-slate-700">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* CCBP — Not Included */}
                    <div className="rounded-2xl overflow-hidden border border-slate-200 opacity-50">
                      <div className="bg-slate-400 text-white text-xs font-bold px-4 py-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2"><span>💳</span> Credit Card Bill Payments</div>
                        <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">✕ Not Included</span>
                      </div>
                      {planBenefits.SILVER.ccbp.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className="flex justify-between items-center px-4 py-2 text-xs bg-white">
                            <span className="text-slate-300 line-through">{range?.trim()}</span>
                            <span className="font-bold text-slate-300 line-through">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 mt-2">
                      <button
                        onClick={(e) => activateWithPlan("SILVER", e)}
                        disabled={loading}
                        className="w-full py-3 rounded-2xl bg-gradient-to-r from-slate-700 to-slate-500 text-white text-sm font-bold hover:opacity-90 disabled:opacity-50 transition"
                      >
                        {loading && selectedPlan === "SILVER" ? "Processing..." : "Activate Subscription"}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRenewSubscription(); }}
                        disabled={renewLoading}
                        className="w-full py-3 rounded-2xl border-2 border-slate-300 text-slate-600 text-sm font-bold hover:bg-slate-50 disabled:opacity-50 transition"
                      >
                        {renewLoading ? "Renewing..." : "Renew Monthly"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* GOLD */}
            {(() => {
              const active = selectedPlan === "GOLD";
              return (
                <div
                  onClick={() => setSelectedPlan("GOLD")}
                  className={`relative cursor-pointer rounded-3xl overflow-hidden transition-all duration-300 flex flex-col ${active
                      ? "shadow-2xl scale-[1.02] ring-2 ring-yellow-400"
                      : "shadow-md hover:shadow-xl hover:scale-[1.01] border border-slate-200"
                    }`}
                >
                  {/* Popular Badge */}
                  <div className="absolute top-4 right-4 z-10">
                    <span className="bg-yellow-400 text-yellow-900 text-[10px] font-black px-3 py-1 rounded-full shadow-md uppercase tracking-wide">
                      {active ? "✓ Selected" : "Popular"}
                    </span>
                  </div>

                  {/* Card Header */}
                  <div className={`p-6 ${active ? "bg-gradient-to-br from-yellow-500 to-amber-400" : "bg-gradient-to-br from-yellow-50 to-amber-100"}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${active ? "bg-white/20 text-white" : "bg-yellow-200 text-yellow-700"}`}>2</div>
                      <div>
                        <p className={`text-xl font-extrabold ${active ? "text-white" : "text-yellow-800"}`}>Plan 2</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-yellow-200 text-yellow-700"}`}>MONTHLY</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className={`text-xs line-through ${active ? "text-yellow-100" : "text-red-400"}`}>₹699</span>
                      <p className={`text-4xl font-black mt-0.5 ${active ? "text-white" : "text-yellow-800"}`}>
                        ₹299 <span className={`text-sm font-semibold ${active ? "text-yellow-100" : "text-yellow-600"}`}>/ month</span>
                      </p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="bg-white flex-1 p-5 flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2">
                      {planBenefits.GOLD.benefits.map((b, i) => (
                        <span key={i} className="flex items-center gap-1 bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs font-semibold px-3 py-1.5 rounded-full">
                          <span className="text-green-500">✓</span> {b}
                        </span>
                      ))}
                      <span className="flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-400 text-xs font-semibold px-3 py-1.5 rounded-full line-through">
                        <span className="text-red-400">✕</span> Credit Card Bill Payments
                      </span>
                    </div>

                    <div className="rounded-2xl overflow-hidden border border-yellow-100">
                      <div className="bg-yellow-500 text-white text-xs font-bold px-4 py-2 flex items-center gap-2">
                        <span>🏦</span> AEPS Commission Slabs
                      </div>
                      {planBenefits.GOLD.aeps.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className={`flex justify-between items-center px-4 py-2 text-xs ${i % 2 === 0 ? "bg-white" : "bg-yellow-50"}`}>
                            <span className="text-slate-500">{range?.trim()}</span>
                            <span className="font-bold text-slate-700">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="rounded-2xl overflow-hidden border border-amber-100">
                      <div className="bg-amber-500 text-white text-xs font-bold px-4 py-2 flex items-center gap-2">
                        <span>💸</span> DMT — Domestic Money Transfer
                      </div>
                      {planBenefits.GOLD.dmt.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className="flex justify-between items-center px-4 py-2 text-xs bg-white">
                            <span className="text-slate-500">{range?.trim()}</span>
                            <span className="font-bold text-slate-700">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* CCBP — Not Included */}
                    <div className="rounded-2xl overflow-hidden border border-slate-200 opacity-50">
                      <div className="bg-slate-400 text-white text-xs font-bold px-4 py-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2"><span>💳</span> Credit Card Bill Payments</div>
                        <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">✕ Not Included</span>
                      </div>
                      {planBenefits.GOLD.ccbp.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className="flex justify-between items-center px-4 py-2 text-xs bg-white">
                            <span className="text-slate-300 line-through">{range?.trim()}</span>
                            <span className="font-bold text-slate-300 line-through">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 mt-2">
                      <button
                        onClick={(e) => activateWithPlan("GOLD", e)}
                        disabled={loading}
                        className="w-full py-3 rounded-2xl bg-gradient-to-r from-yellow-500 to-amber-400 text-white text-sm font-bold hover:opacity-90 disabled:opacity-50 transition shadow-md"
                      >
                        {loading && selectedPlan === "GOLD" ? "Processing..." : "Activate Subscription"}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRenewSubscription(); }}
                        disabled={renewLoading}
                        className="w-full py-3 rounded-2xl border-2 border-yellow-300 text-yellow-700 text-sm font-bold hover:bg-yellow-50 disabled:opacity-50 transition"
                      >
                        {renewLoading ? "Renewing..." : "Renew Monthly"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* PLATINUM */}
            {(() => {
              const active = selectedPlan === "PLATINUM";
              return (
                <div
                  onClick={() => setSelectedPlan("PLATINUM")}
                  className={`relative cursor-pointer rounded-3xl overflow-hidden transition-all duration-300 flex flex-col ${active
                      ? "shadow-2xl scale-[1.02] ring-2 ring-indigo-400"
                      : "shadow-md hover:shadow-xl hover:scale-[1.01] border border-slate-200"
                    }`}
                >
                  {active && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="bg-white text-indigo-700 text-[10px] font-black px-3 py-1 rounded-full shadow-md">✓ Selected</span>
                    </div>
                  )}

                  {/* Card Header */}
                  <div className={`p-6 ${active ? "bg-gradient-to-br from-indigo-700 to-purple-600" : "bg-gradient-to-br from-indigo-50 to-purple-100"}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${active ? "bg-white/20 text-white" : "bg-indigo-200 text-indigo-700"}`}>3</div>
                      <div>
                        <p className={`text-xl font-extrabold ${active ? "text-white" : "text-indigo-800"}`}>Plan 3</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-indigo-200 text-indigo-700"}`}>MONTHLY</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className={`text-xs line-through ${active ? "text-indigo-200" : "text-red-400"}`}>₹1300</span>
                      <p className={`text-4xl font-black mt-0.5 ${active ? "text-white" : "text-indigo-800"}`}>
                        ₹999 <span className={`text-sm font-semibold ${active ? "text-indigo-200" : "text-indigo-500"}`}>/ month</span>
                      </p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="bg-white flex-1 p-5 flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2">
                      {planBenefits.PLATINUM.benefits.map((b, i) => (
                        <span key={i} className="flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-semibold px-3 py-1.5 rounded-full">
                          <span className="text-green-500">✓</span> {b}
                        </span>
                      ))}
                    </div>

                    {/* AEPS Slabs */}
                    <div className="rounded-2xl overflow-hidden border border-indigo-100">
                      <div className="bg-indigo-600 text-white text-xs font-bold px-4 py-2 flex items-center gap-2">
                        <span>🏦</span> AEPS Commission Slabs
                      </div>
                      {planBenefits.PLATINUM.aeps.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className={`flex justify-between items-center px-4 py-2 text-xs ${i % 2 === 0 ? "bg-white" : "bg-indigo-50"}`}>
                            <span className="text-slate-500">{range?.trim()}</span>
                            <span className="font-bold text-slate-700">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* DMT */}
                    <div className="rounded-2xl overflow-hidden border border-amber-100">
                      <div className="bg-amber-500 text-white text-xs font-bold px-4 py-2 flex items-center gap-2">
                        <span>💸</span> DMT — Domestic Money Transfer
                      </div>
                      {planBenefits.PLATINUM.dmt.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className="flex justify-between items-center px-4 py-2 text-xs bg-white">
                            <span className="text-slate-500">{range?.trim()}</span>
                            <span className="font-bold text-slate-700">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* CCBP — Platinum Exclusive */}
                    <div className="rounded-2xl overflow-hidden border border-red-100">
                      <div className="bg-red-500 text-white text-xs font-bold px-4 py-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2"><span>💳</span> Credit Card Bill Payments</div>
                      </div>
                      {planBenefits.PLATINUM.ccbp.map((item, i) => {
                        const [range, amt] = item.split("→");
                        return (
                          <div key={i} className={`flex justify-between items-center px-4 py-2 text-xs ${i % 2 === 0 ? "bg-white" : "bg-red-50"}`}>
                            <span className="text-slate-500">{range?.trim()}</span>
                            <span className="font-bold text-slate-700">{amt?.trim()}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 mt-2">
                      <button
                        onClick={(e) => activateWithPlan("PLATINUM", e)}
                        disabled={loading}
                        className="w-full py-3 rounded-2xl bg-gradient-to-r from-indigo-700 to-purple-600 text-white text-sm font-bold hover:opacity-90 disabled:opacity-50 transition shadow-md"
                      >
                        {loading && selectedPlan === "PLATINUM" ? "Processing..." : "Activate Subscription"}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRenewSubscription(); }}
                        disabled={renewLoading}
                        className="w-full py-3 rounded-2xl border-2 border-indigo-300 text-indigo-700 text-sm font-bold hover:bg-indigo-50 disabled:opacity-50 transition"
                      >
                        {renewLoading ? "Renewing..." : "Renew Monthly"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

          </div>
        </div>


      </div>

      {/* SUBSCRIPTION HISTORY */}
      <div className="max-w-6xl mx-auto mt-8">
        <div className="bg-white rounded-3xl shadow-md border border-slate-200 overflow-hidden">

          {/* History Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
            <div>
              <h2 className="text-xl font-bold text-[#12345A]">Subscription History</h2>
              <p className="text-xs text-slate-400 mt-0.5">Your latest subscription transactions</p>
            </div>
            <button
              onClick={fetchSubscriptionHistory}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#12345A] text-white text-sm font-semibold hover:opacity-90 transition"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          {historyLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-slate-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : subscriptionHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Date</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Txn ID</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Plan</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Duration</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Amount</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">GST</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Total</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Remaining</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Auto Renewal</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Status</th>
                    <th className="px-5 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionHistory.map((item, index) => (
                    <tr
                      key={index}
                      className={`border-b border-slate-50 hover:bg-blue-50/40 transition-colors ${item.isActive ? "bg-green-50/30" : ""}`}
                    >
                      <td className="px-5 py-4 whitespace-nowrap text-xs text-slate-500">
                        {item.subscribedAt
                          ? new Date(item.subscribedAt).toLocaleString("en-IN")
                          : "-"}
                      </td>

                      <td className="px-5 py-4 font-mono text-xs text-slate-600 max-w-[140px] truncate" title={item.txnId}>
                        {item.txnId || "-"}
                      </td>

                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${item.subscriptionId === "PLATINUM"
                            ? "bg-indigo-100 text-indigo-700"
                            : item.subscriptionId === "GOLD"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-slate-100 text-slate-600"
                          }`}>
                          {item.subscriptionId === "PLATINUM" ? "Plan 3" : item.subscriptionId === "GOLD" ? "Plan 2" : "Plan 1"}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-xs text-slate-600 whitespace-nowrap">
                        {item.durationDays} Days
                      </td>

                      <td className="px-5 py-4 font-semibold text-green-600 whitespace-nowrap text-xs">
                        ₹{item.netAmount}
                      </td>

                      <td className="px-5 py-4 text-xs text-slate-500 whitespace-nowrap">
                        ₹{item.gstAmount}
                      </td>

                      <td className="px-5 py-4 font-bold text-[#12345A] whitespace-nowrap text-xs">
                        ₹{item.txnAmount}
                      </td>

                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className={`text-xs font-semibold ${item.remainingDays > 0 ? "text-blue-600" : "text-slate-400"}`}>
                          {item.remainingDays} Days
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${item.isAutoRenewal
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-500"
                          }`}>
                          {item.isAutoRenewal ? "On" : "Off"}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${item.status === "SUCCESS"
                            ? "bg-green-100 text-green-700"
                            : item.status === "CLOSED"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-red-100 text-red-600"
                          }`}>
                          {item.status === "SUCCESS" ? "Success" : item.status}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${item.isActive
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-400"
                          }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${item.isActive ? "bg-emerald-500" : "bg-slate-300"}`} />
                          {item.isActive ? "Active" : "Closed"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-300">
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm text-slate-400 font-medium">No subscription history found</p>
            </div>
          )}
        </div>
      </div>

      {/* CUSTOM POPUP MODAL */}
      {popup.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#12345A]/60 backdrop-blur-sm z-[200]">
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-sm w-full shadow-2xl animate-fade-in-up ">

            {/* Popup Icon */}
            <div
              className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl mb-5 ${popup.type === "success"
                ? "bg-green-100 text-green-500"
                : "bg-red-100 text-red-500"
                }`}
            >
              {popup.type === "success" ? "✓" : "✕"}
            </div>

            {/* Popup Title */}
            <h3 className="text-xl font-bold text-[#12345A] text-center mb-2">
              {popup.type === "success" ? "Success" : "Error"}
            </h3>

            {/* Popup Message */}
            <p className="text-slate-600 text-center text-sm mb-8 leading-relaxed">
              {popup.message}
            </p>

            {/* Popup Button */}
            <button
              onClick={closePopup}
              className="w-full bg-[#12345A] hover:bg-[#1E4D8C] text-white py-3 rounded-2xl font-semibold transition shadow-md"
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;